// User-side reasons a request can fail before (or without) the server ever
// answering - offline, weak/slow network, timeouts, blocked requests,
// cookies disabled. Shared by both axios interceptors (utils/ApiService.tsx
// and helpers/api/apiCore.ts) so every screen shows the same wording.

export const NETWORK_MESSAGES = {
  OFFLINE:
    "You are offline. Please check your internet connection (Wi-Fi or mobile data) and try again.",
  SLOW_CONNECTION:
    "Your internet connection is slow or weak, so the request took too long. Please move to a better network and try again.",
  TIMEOUT:
    "The request took too long to complete. Your connection may be slow - please try again.",
  BLOCKED:
    "Unable to reach the server. Please check your internet connection. If you are using a VPN, firewall, proxy or ad-blocker, try turning it off and try again.",
  COOKIES_DISABLED:
    "Cookies are disabled in your browser. Please enable cookies for this site and log in again.",
  CANCELLED: "The request was cancelled.",
} as const;

export type NetworkErrorCode = keyof typeof NETWORK_MESSAGES;

// Network Information API - Chrome/Edge/Android only; undefined elsewhere.
const getConnection = (): any =>
  typeof navigator !== "undefined"
    ? (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection
    : undefined;

export const isSlowConnection = (): boolean => {
  const connection = getConnection();
  if (!connection) return false;
  return (
    connection.saveData === true ||
    ["slow-2g", "2g"].includes(connection.effectiveType) ||
    (typeof connection.downlink === "number" && connection.downlink > 0 && connection.downlink < 0.5) ||
    (typeof connection.rtt === "number" && connection.rtt > 1500)
  );
};

export const isOffline = (): boolean =>
  typeof navigator !== "undefined" && navigator.onLine === false;

export const areCookiesDisabled = (): boolean =>
  typeof navigator !== "undefined" && navigator.cookieEnabled === false;

// For an axios error that has NO `error.response` (the server never replied).
export const getClientSideErrorCode = (error: any): NetworkErrorCode => {
  if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return "CANCELLED";
  if (isOffline()) return "OFFLINE";
  const timedOut = error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT";
  if (timedOut) return isSlowConnection() ? "SLOW_CONNECTION" : "TIMEOUT";
  if (isSlowConnection()) return "SLOW_CONNECTION";
  return "BLOCKED";
};

// The session cookie can't be sent if cookies are off, which surfaces as a
// 401/440 from the server - point the user at the real cause instead.
export const getSessionErrorMessage = (fallback: string): string =>
  areCookiesDisabled() ? NETWORK_MESSAGES.COOKIES_DISABLED : fallback;

// GETs get a timeout so a stalled request on a weak network ends with a clear
// message instead of a spinner that never stops. Uploads/writes are left
// without one - a large file on a slow line can legitimately take minutes.
export const GET_REQUEST_TIMEOUT_MS = 45000;
